import React from "react";
import { Raycast, Vec2, Vec3 } from "ogl";
import { defaultIndexer } from "@core/systems/utils/default";
import { each, find, map, pipe } from "@fxts/core";
import { OglCanvas } from "@ogl-engine/OglCanvas";
import { MapControl } from "@ogl-engine/MapControl";
import { assetLoader } from "@ogl-engine/AssetLoader";
import { Skybox } from "@ogl-engine/materials/skybox/skybox";
import { Path } from "@ogl-engine/utils/path";
import type { SkyboxTexture } from "@assets/textures/skybox";
import { TacticalMapScene } from "@ogl-engine/engine/Scene";
import type { Sim } from "@core/sim";
import { contextMenuStore } from "@ui/state/contextMenu";
import { storageHook } from "@core/hooks";
import { MouseButton } from "@ogl-engine/Orbit";
import { Asteroids } from "@ogl-engine/builders/Asteroids";
import { Engine3D } from "@ogl-engine/engine/engine3d";
import { gameStore } from "@ui/state/game";
import { reaction } from "mobx";
import { Star } from "@ogl-engine/builders/Star";
import type { Entity } from "@core/entity";
import { SelectionBox } from "@ogl-engine/builders/SelectionBox";
import sounds from "@assets/ui/sounds";
import { transport3D } from "@core/systems/transport3d";
import type { GameSettings } from "@core/settings";
import { defaultGameSttings } from "@core/settings";
import merge from "lodash/merge";
import { renderLogger } from "@core/log";
import { DustCloud } from "@ogl-engine/builders/DustCloud";
import { mineableCommoditiesArray } from "@core/economy/commodity";
import { entityIndexer } from "@core/entityIndexer/entityIndexer";
import { asteroidFieldComponents } from "@core/archetypes/asteroidField";
import { Planet } from "@ogl-engine/builders/Planet";
import { EntityMesh, distanceScale } from "./EntityMesh";
import { createShootHandler } from "./events/shoot";
import { createExplodeHandler } from "./events/explode";
import { createDeployFacilityHandler } from "./events/deployFacility";
import type { Prop } from "../../../core/world/map";
import mapData from "../../../core/world/map";
import { createStartMiningHandler } from "./events/startMining";
import { createStopMiningHandler } from "./events/stopMining";

const tempCameraFocus = new Vec3();

interface TacticalMapProps {
  sim: Sim;
  engine?: Engine3D<TacticalMapScene>;
}

export class TacticalMap extends React.PureComponent<TacticalMapProps> {
  engine: Engine3D<TacticalMapScene>;
  sim: Sim;
  raycast = new Raycast();
  raycastHits: EntityMesh[] = [];
  lastClicked = 0;
  control: MapControl;
  meshes: WeakMap<Entity, EntityMesh> = new Map();
  dragStart: Vec2 | null = null;
  selectionBox: SelectionBox;
  logger = renderLogger.sub("tactical");

  onUnmountCallbacks: (() => void)[] = [];

  constructor(props: TacticalMapProps) {
    super(props);
    this.sim = props.sim;
    this.engine = props.engine ?? new Engine3D<TacticalMapScene>();
    this.engine.setScene(new TacticalMapScene(this.engine));
    transport3D.subscribe("shoot", createShootHandler(this.engine, this.sim));
    transport3D.subscribe("explode", createExplodeHandler(this.engine));
    transport3D.subscribe(
      "deployFacility",
      createDeployFacilityHandler(this.engine, this.meshes)
    );
    transport3D.subscribe(
      "startMining",
      createStartMiningHandler(this.engine, this.meshes)
    );
    transport3D.subscribe(
      "stopMining",
      createStopMiningHandler(this.engine, this.meshes)
    );
    const onSpeedChange = (speed: number) => {
      this.engine.setDeltaMultiplier(speed);
    };
    const unsubscribe = this.sim.hooks.subscribe(
      "speedChange",
      ({ newSpeed }) => {
        onSpeedChange(newSpeed);
      }
    );
    this.onUnmountCallbacks.push(
      transport3D.reset.bind(transport3D),
      unsubscribe
    );
  }

  componentDidMount(): void {
    gameStore.setSector(
      find(
        (s) => s.cp.name.value === "Teegarden's Star II",
        this.sim.index.sectors.get()
      ) ?? this.sim.index.sectors.get()[0]
    );

    this.engine.hooks.subscribe("init", () => {
      this.onEngineInit();
    });
    this.engine.hooks.subscribe("update", () => this.onEngineUpdate());
    this.onUnmountCallbacks.push(
      reaction(
        () => gameStore.sector,
        () => {
          if (this.engine.initialized) {
            this.onSectorChange();
          }
        }
      )
    );
    this.onUnmountCallbacks.push(
      reaction(() => gameStore.selectedUnits, this.onSelectedChange.bind(this))
    );
    storageHook.subscribe("TacticalMap", (key) => {
      if (key !== "gameSettings") return;
      this.updateEngineSettings();
    });
    this.sim.hooks.subscribe("removeEntity", ({ entity }) => {
      if (this.meshes.has(entity)) {
        const mesh = this.meshes.get(entity)!;
        mesh.destroy();
        this.engine.scene.entities.removeChild(mesh);
        this.meshes.delete(entity);
        if (
          entity.hasComponents(["position"]) &&
          gameStore.selectedUnits.includes(entity)
        ) {
          gameStore.unselectUnit(entity);
        }
      }
    });
  }

  componentWillUnmount(): void {
    this.onUnmountCallbacks.forEach((cb) => cb());
  }

  handleEntityClick(multiple: boolean) {
    if (this.raycastHits.length) {
      let mesh = this.raycastHits[0];
      if (
        gameStore.selectedUnits[0]?.id === mesh.entityId &&
        this.raycastHits.length > 1
      ) {
        mesh = this.raycastHits[1];
      }

      const isDoubleClick = Date.now() - this.lastClicked < 200;

      const entity = this.sim
        .getOrThrow(mesh.entityId)
        .requireComponents(["position"]);
      if (
        multiple &&
        entity.cp.owner?.id === this.sim.index.player.get()[0]?.id
      ) {
        if (gameStore.selectedUnits.includes(entity)) {
          gameStore.unselectUnit(entity);
        } else {
          gameStore.addSelectedUnit(entity);
        }
      } else {
        gameStore.setSelectedUnits([entity]);
        gameStore.unfocus();
      }
      sounds.click.play();

      if (isDoubleClick) {
        gameStore.focus();
      }

      this.lastClicked = Date.now();
    } else if (!this.control.keysPressed.has("ShiftLeft")) {
      gameStore.unfocus();
      gameStore.clearSelection();
    }
  }

  async onEngineUpdate() {
    if (!(assetLoader.ready && this.engine.isFocused() && this.control)) return;

    if (this.dragStart) {
      const normalisedDragStart = new Vec2(
        2.0 * (this.dragStart.x / this.engine.gl.renderer.width) - 1.0,
        2.0 * (1.0 - this.dragStart.y / this.engine.gl.renderer.height) - 1.0
      );

      const normalisedMousePos = new Vec2(
        2.0 * (this.control!.mouse.x / this.engine.gl.renderer.width) - 1.0,
        2.0 * (1.0 - this.control!.mouse.y / this.engine.gl.renderer.height) -
          1.0
      );
      this.selectionBox.updateGeometry(normalisedDragStart, normalisedMousePos);
    }
    this.updateRenderables();
    this.updateRaycast();

    this.engine.canvas.style.cursor = this.raycastHits.length
      ? "pointer"
      : "default";

    this.updateUIElements();
    this.updateFocus();
    this.control!.update(this.engine.originalDelta);
  }

  onRightClick() {
    const targetId = this.raycastHits.length
      ? this.raycastHits[0].entityId
      : null;
    const worldPos = this.raycast.intersectPlane({
      origin: new Vec3(0),
      normal: new Vec3(0, 1, 0),
    });
    const worldPosition = new Vec2(
      worldPos.x / distanceScale,
      worldPos.z / distanceScale
    );

    contextMenuStore.open({
      position: this.control.mouse.clone(),
      worldPosition,
      sector: gameStore.sector,
      target: targetId ? this.sim.getOrThrow(targetId) : null,
    });
  }

  async onEngineInit() {
    await assetLoader.load(this.engine.gl);

    this.control = new MapControl(this.engine.camera, this.engine.canvas);
    this.control.onPointerUp = this.onPointerUp.bind(this);
    this.control.onPointerDown = this.onControlClick.bind(this);
    this.control.onRightClick = this.onRightClick.bind(this);
    this.control.onPan = () => {
      gameStore.unfocus();
    };
    this.control.isFocused = this.engine.isFocused.bind(this.engine);
    this.control.onKeyDown = this.onKeyDown.bind(this);

    this.updateEngineSettings();
    this.loadSector();

    if (process.env.STORYBOOK) {
      this.engine.scene.addGrid();
    }
  }

  onPointerUp(position: Vec2, button: MouseButton, isTarget: boolean) {
    if (
      button === MouseButton.Left &&
      this.dragStart &&
      this.dragStart!.distance(position) > 0.1
    ) {
      gameStore.setSelectionBoxState(false);
      this.removeSelectionBox();

      gameStore.setSelectedUnits(
        this.selectionBox
          .getEntitiesInSelection()
          .map(({ entityId }) =>
            this.sim.getOrThrow(entityId).requireComponents(["position"])
          )
          .filter((e) => e.cp.owner?.id === this.sim.index.player.get()[0]?.id)
      );
    } else if (button === MouseButton.Left && isTarget) {
      gameStore.setSelectionBoxState(false);
      this.removeSelectionBox();
      this.handleEntityClick(this.control.keysPressed.has("ShiftLeft"));
    }
    this.dragStart = null;
  }

  async onControlClick(mousePosition: Vec2, button: MouseButton) {
    if (button === MouseButton.Left) {
      this.dragStart = mousePosition.clone();
      this.drawSelectionBox();
      gameStore.setSelectionBoxState(true);
    }
  }

  // eslint-disable-next-line class-methods-use-this
  onKeyDown(event: KeyboardEvent) {
    if (event.code === "KeyR" && gameStore.selectedUnits.length) {
      gameStore.focus();
    }
  }

  onSectorChange() {
    this.engine.scene.destroy();
    this.engine.scene.setParent(null);
    this.engine.setScene(new TacticalMapScene(this.engine));
    this.loadSector();
  }

  onSelectedChange(entities: Entity[], prevEntities: Entity[]) {
    for (const entity of prevEntities) {
      this.meshes.get(entity)?.setSelected(false);
      const path = this.engine.scene.ui.children.find(
        (c) => c instanceof Path && c.owner === entity
      );
      if (path) {
        this.engine.scene.ui.removeChild(path);
      }
    }

    for (const entity of entities) {
      this.meshes.get(entity)?.setSelected(true);
      if (entity.hasComponents(["position", "orders"])) {
        const path = new Path(this.engine, entity);
        this.engine.scene.ui.addChild(path);
      }
    }
  }

  drawSelectionBox() {
    this.selectionBox.setVisibility(true);
  }

  removeSelectionBox() {
    this.selectionBox.setVisibility(false);
  }

  updateRaycast() {
    const normalisedMousePos = new Vec2(
      2.0 * (this.control!.mouse.x / this.engine.gl.renderer.width) - 1.0,
      2.0 * (1.0 - this.control!.mouse.y / this.engine.gl.renderer.height) - 1.0
    );
    this.raycast.castMouse(this.engine.camera, normalisedMousePos);
    this.raycastHits = this.raycast.intersectBounds(
      this.engine.scene.entities.children
    ) as EntityMesh[];

    for (const entity of this.engine.scene.entities.children) {
      if (entity instanceof EntityMesh) {
        entity.setHovered(this.raycastHits.includes(entity));
      }
    }
  }

  updateRenderables() {
    for (const entity of defaultIndexer.renderable.getIt()) {
      if (entity.cp.position.sector !== gameStore.sector.id) {
        if (this.meshes.has(entity)) {
          this.meshes.get(entity)!.destroy();
          this.engine.scene.entities.removeChild(this.meshes.get(entity)!);
          this.meshes.delete(entity);
        }
        continue;
      }

      // if (!(entity.cp.render.model in assetLoader.models)) {
      //   entity.cp.render.model = `ship/${entity.cp.model?.slug}` as any;
      // }

      // FIXME: Remove this debug code
      if (!(entity.cp.render.model in assetLoader.models)) {
        // eslint-disable-next-line no-console
        this.logger.log(`Missing model: ${entity.cp.render.model}`, "warn");
        if (entity.hasComponents(["dockable"])) {
          entity.cp.render.model = "ship/dart";

          if (entity.cp.dockable.size === "medium") {
            entity.cp.render.model = entity.tags.has("role:mining")
              ? "ship/marble"
              : entity.tags.has("role:military")
              ? "ship/axe"
              : "ship/mCiv";
          }

          if (entity.cp.dockable.size === "large") {
            entity.cp.render.model = "ship/lMil";
          }

          if (entity.cp.model?.slug === "dart") {
            entity.cp.render.model = "ship/dart";
          }
        } else if (entity.hasTags(["facility"])) {
          entity.cp.render.model = "facility/default";
          if (entity.tags.has("gateway")) {
            entity.cp.render.model = "facility/gateway";
          }
        } else {
          // FIXME: This is just a placeholder for now
          entity.cp.render.model = "world/asteroid1";
        }
      }

      if (!this.meshes.has(entity)) {
        const mesh = new EntityMesh(this.engine, entity);
        mesh.scale.multiply(entity.cp.render.defaultScale);
        this.engine.scene.entities.addChild(mesh);
        this.meshes.set(entity, mesh);
      }
    }
  }

  updateUIElements() {
    for (const path of this.engine.scene.ui.children.filter(
      (c) => c instanceof Path
    ) as Path[]) {
      path.update(
        path.getPath(path.owner.requireComponents(["position", "orders"]))
      );
    }
  }

  updateFocus() {
    if (
      gameStore.focused &&
      gameStore.selectedUnits.length &&
      gameStore.selectedUnits.every(
        (e) =>
          e.cp.position.sector === gameStore.selectedUnits[0].cp.position.sector
      )
    ) {
      if (
        gameStore.sector.id !==
          gameStore.selectedUnits[0]?.cp.position!.sector &&
        gameStore.selectedUnits.length === 1
      ) {
        gameStore.setSector(
          this.sim.getOrThrow(gameStore.selectedUnits[0].cp.position!.sector)
        );
      }

      tempCameraFocus.set(0);
      pipe(
        gameStore.selectedUnits,
        map((e) => this.engine.getByEntityId(e.id)!.position),
        each((val) => tempCameraFocus.add(val))
      );
      tempCameraFocus.scale(1 / gameStore.selectedUnits.length);

      if (
        !this.control.transition.active &&
        gameStore.focusType === "transition"
      ) {
        this.control!.transitionTo(
          {
            position: tempCameraFocus,
            rotation: this.engine.camera.rotation,
          },
          0.15,
          () => {
            gameStore.focusType = "lookAt";
          }
        );
      } else if (gameStore.focusType === "lookAt") {
        this.control!.lookAt(tempCameraFocus);
      }
    }
  }

  updateEngineSettings() {
    const settings: GameSettings = merge(
      {},
      defaultGameSttings,
      JSON.parse(localStorage.getItem("gameSettings")!)
    );

    this.engine.fxaa = settings.graphics.fxaa;
    this.engine.godrays = settings.graphics.godrays;
  }

  loadSector() {
    this.loadSkybox();
    this.loadAsteroidFields();
    this.loadProps();

    this.selectionBox = new SelectionBox(this.engine);
    this.selectionBox.setParent(this.engine.scene.ui);

    if (!gameStore.focused) {
      this.control.lookAt(new Vec3(0, 0, 0));
    }
  }

  loadSkybox() {
    if (!this.engine.scene.skybox) {
      this.engine.scene.addSkybox(
        new Skybox(
          this.engine,
          (mapData.sectors.find((s) => s.id === gameStore.sector.cp.name.slug)
            ?.skybox as SkyboxTexture) ?? "deepspace1"
        )
      );
    }
  }

  loadAsteroidFields() {
    const fields = entityIndexer.searchBySector(
      gameStore.sector.id,
      asteroidFieldComponents
    );
    for (const field of fields) {
      const fieldTransform = new Asteroids(
        this.engine,
        field.cp.mineable.size,
        field.cp.mineable.density,
        field.cp.mineable.fPoints,
        mineableCommoditiesArray.filter(
          (m) => field.cp.mineable.resources[m] > 0
        )
      );
      fieldTransform.position.set(
        field.cp.position.coord[0] * distanceScale,
        0,
        field.cp.position.coord[1] * distanceScale
      );
      fieldTransform.scale.set(1);
      this.engine.scene.addChild(fieldTransform);
    }
  }

  loadProp(data: Prop) {
    if (data.type === "star") {
      const star = new Star(this.engine, data.color);
      star.body.material.setColor2(data.color2);
      star.updatePositionFromSphericalCoords(
        data.position[0],
        data.position[1],
        data.position[2]
      );
      star.scale.set(data.scale);
      star.body.material.uniforms.uNoise.value = data.noise;
      star.body.material.uniforms.uNoisePower.value = data.noisePower;

      if (data.corona) {
        star.corona.material.uniforms.uX.value = data.corona[0];
        star.corona.material.uniforms.uY.value = data.corona[1];
      }

      return star;
    }

    if (data.type === "planet") {
      const planet = new Planet(this.engine, data.textureSet);
      planet.updatePositionFromSphericalCoords(
        data.position[0],
        data.position[1],
        data.position[2]
      );
      planet.scale.set(data.scale);
      planet.atmosphere.material.uniforms.uX.value = data.atmosphere[0];
      planet.atmosphere.material.uniforms.uY.value = data.atmosphere[1];

      return planet;
    }

    if (data.type === "dust") {
      const dust = new DustCloud(this.engine, data.size, data.density);
      dust.position.set(data.position[0], data.position[1], data.position[2]);
      dust.scale.set(distanceScale);
      dust.material.setColor(data.color);

      return dust;
    }

    throw new Error("Unknown prop type");
  }

  loadProps() {
    const data = mapData.sectors.find(
      (s) => s.id === gameStore.sector.cp.name.slug
    );
    if (!data?.props) return;

    for (const prop of data.props) {
      const propObject = this.loadProp(prop);

      if (prop.name) propObject.name = prop.name;
      if ((propObject as any).createPaneFolder)
        (propObject as any).createPaneFolder();
      this.engine.scene.props.addChild(propObject);
    }
  }

  render() {
    return <OglCanvas engine={this.engine} />;
  }
}
